import {
  CreateOrganizationInterface,
  MembershipLevelType,
  OrganizationInterface,
  OrganizationPatchInterface,
} from "../types";
import { AxiosResponse } from "axios";
import api from "./index";

export const getOrganizations = async ({
  offset,
  search,
  count,
}: {
  offset?: number;
  search?: string;
  count?: number;
}) => {
  try {
    const {
      data: { data, meta },
    }: { data: { data: OrganizationInterface; meta: { count: number } } } =
      await api.get(
        `/v2/user/organizations?skipProducts=true&skipVoucherTypeConfig=true&count=${
          count || 20
        }&offset=${offset ? offset : 0}${search ? `&search=${search}` : ""}`
      );
    return { data, meta };
  } catch (err: unknown) {
    throw err;
  }
};

export const getOrganization = async (id: string | number): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get(`/v1/organizations/${id}`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const deleteOrganization = async (id: number): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.delete(`/v1/organizations/${id}`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const getOrganizationFields = async (
  id: string | number
): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get(`/v1/organizations/${id}/fields`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const getProducts = async (): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get("/v1/products");
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const getOrganizationUsers = async (
  id: string | number
): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get(`/v1/organizations/${id}/memberships`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const getOrganizationInvitations = async (
  id: string | number
): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get(`/v1/organizations/${id}/invitations`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const postInviteUser = async ({
  id,
  email,
  level,
}: {
  id: number | string;
  email: string;
  level: string;
}): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.post(`/v1/organizations/${id}/invitations`, {
      email,
      level,
    });
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const createOrganization = async (
  values: CreateOrganizationInterface
): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.post(`/v1/user/organizations`, values);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const deleteUserInvitation = async (id: number): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.delete(`/v1/invitations/${id}`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const deleteMembership = async (id: number): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.delete(`/v1/memberships/${id}`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const addProduct = async ({
  organizationId,
  productId,
}: {
  organizationId: number;
  productId: number;
}): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.post(
      `/v1/organizations/${organizationId}/products`,
      {
        product_id: productId,
      }
    );
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const deleteProduct = async ({
  organizationId,
  productId,
}: {
  organizationId: number;
  productId: number;
}): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.delete(
      `/v1/organizations/${organizationId}/products/${productId}`
    );
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const updateOrganizationProductFields = async ({
  organizationId,
  fields,
}: {
  organizationId: number;
  fields: { id: number; validation?: boolean }[];
}): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.put(
      `/v2/organizations/${organizationId}/fields`,
      fields
    );
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const updateOrganizationInfo = async ({
  organizationId,
  organizationData,
}: {
  organizationId: number;
  organizationData: OrganizationPatchInterface;
}): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.patch(
      `/v1/organizations/${organizationId}`,
      organizationData
    );
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const getCreditorCatalog = async (id: number): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.get(`/v1/organizations/${id}/creditorCatalog`);
    return data;
  } catch (err: unknown) {
    throw err;
  }
};

export const updateMembership = async (
  membershipId: number,
  membershipData: { label: string; level: MembershipLevelType }
): Promise<any> => {
  try {
    const {
      data: { data },
    }: AxiosResponse = await api.patch(
      `/v1/memberships/${membershipId}`,
      membershipData
    );
    return data;
  } catch (err: unknown) {
    throw err;
  }
};
